// css
import styles from './styles.module.scss';

// components
import { Image, Button } from '@mantine/core';
import { DoorOpen, ChevronRight } from 'lucide-react';
import GameSimilar from '../GameSimilar';
import GameDescription from '../GameDescription';
import Head from 'next/head';

// hooks
import { useRouter } from 'next/router';
import { useEffect, useState, useRef } from 'react';
import fetchGameCover from '@/utils/fetch/fetch.js';
import { useSelector, useDispatch } from 'react-redux';
import { recordCategoryClick } from '@/store/features/gameSlice';

// api
import { getSimilarGames } from '@/api/header';

// 像素风箭头组件
const PixelArrow = ({ className }) => (
    <div className={`${styles.pixel_arrow} ${className}`}>
        <div className={styles.pixel_arrow_body}></div>
    </div>
);

export default function GameDetailMobile({ game, gameCoverLoading, similarGamesProps }) {
    const { categoryClickData } = useSelector((state) => state.game);
    const dispatch = useDispatch();
    const [similarGames, setSimilarGames] = useState(similarGamesProps || []);
    const [loading, setLoading] = useState(!similarGamesProps?.length);
    const [showIframe, setShowIframe] = useState(false);
    const [isButtonExpanded, setIsButtonExpanded] = useState(false);
    const iframeRef = useRef(null);
    const autoCollapseTimerRef = useRef(null);
    const router = useRouter();

    // 展开按钮并设置自动收缩定时器
    const expandButton = () => {
        setIsButtonExpanded(true);
        
        // // 清除之前的定时器
        if (autoCollapseTimerRef.current) {
            clearTimeout(autoCollapseTimerRef.current);
        }
        
        // 设置5秒后自动收缩
        autoCollapseTimerRef.current = setTimeout(() => {
            setIsButtonExpanded(false);
        }, 5000);
    };

    // 收缩按钮
    const collapseButton = () => {
        setIsButtonExpanded(false);
        if (autoCollapseTimerRef.current) {
            clearTimeout(autoCollapseTimerRef.current);
            autoCollapseTimerRef.current = null;
        }
    };

    // 点击Play Now显示全屏游戏
    const handlePlayNow = () => {
        setShowIframe(true);
        // 防止页面滚动
        document.body.style.overflow = 'hidden';
        // 展开关闭按钮
        expandButton();
    };

    // 关闭全屏游戏
    const handleCloseIframe = (event) => {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        setShowIframe(false);
        // 恢复页面滚动
        document.body.style.overflow = 'auto';
        // 收缩按钮
        collapseButton();
    };

    // 点击关闭按钮的处理逻辑
    const handleCloseButtonClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        if (isButtonExpanded) {
            // 如果按钮已展开，则关闭游戏
            handleCloseIframe(e);
        } else {
            // 如果按钮是缩进状态，则展开按钮
            expandButton();
        }
    };

    // 监听ESC键关闭全屏
    useEffect(() => {
        const handleEscKey = (event) => {
            if (event.key === 'Escape' && showIframe) {
                event.preventDefault();
                event.stopPropagation();
                handleCloseIframe();
            }
        };

        if (showIframe) {
            // 同时监听window和document
            document.addEventListener('keydown', handleEscKey, true);
            window.addEventListener('keydown', handleEscKey, true);
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey, true);
            window.removeEventListener('keydown', handleEscKey, true);
        };
    }, [showIframe]);

    // 组件卸载时恢复页面滚动
    useEffect(() => {
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    // 组件卸载时清理定时器
    useEffect(() => {
        return () => {
            if (autoCollapseTimerRef.current) {
                clearTimeout(autoCollapseTimerRef.current);
            }
        };
    }, []);

    // 处理移动端视口变化，避免浏览器操作栏遮挡iframe
    useEffect(() => {
        if (!showIframe) return;

        const updateViewportHeight = () => {
            // 获取实际可用的视口高度
            const actualHeight = window.innerHeight;
            const documentHeight = document.documentElement.clientHeight;
            
            // 使用更准确的高度值
            const viewportHeight = Math.min(actualHeight, documentHeight);
            
            // 更新CSS自定义属性
            document.documentElement.style.setProperty('--actual-viewport-height', `${viewportHeight}px`);
            
            // 为不支持dvh的浏览器提供回退
            if (iframeRef.current) {
                const container = iframeRef.current.parentElement;
                if (container) {
                    container.style.height = `${viewportHeight}px`;
                }
            }
        };

        // 初始设置
        updateViewportHeight();

        // 监听视口变化（包括方向改变、浏览器UI显示/隐藏）
        const handleResize = () => {
            // 延迟执行，等待浏览器UI动画完成
            setTimeout(updateViewportHeight, 100);
        };

        const handleOrientationChange = () => {
            // 方向改变时延迟更长时间
            setTimeout(updateViewportHeight, 300);
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleOrientationChange);
        
        // iOS Safari特殊处理
        if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
            // 监听滚动事件来检测Safari工具栏的显示/隐藏
            const handleScroll = () => {
                setTimeout(updateViewportHeight, 50);
            };
            window.addEventListener('scroll', handleScroll, { passive: true });
            
            return () => {
                window.removeEventListener('resize', handleResize);
                window.removeEventListener('orientationchange', handleOrientationChange);
                window.removeEventListener('scroll', handleScroll);
            };
        }

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, [showIframe]);


    return (
        <>
            <div className={styles.container}>
                {/* 游戏封面 */}
                <div className={styles.game_cover}>
                    <Image
                        src={game.cover_image}
                        alt={`Game ${game.name}`}
                        width={256}
                        height={256}
                        className={styles.cover_image}
                    />
                </div>

                {/* 游戏名称 */}
                <h1 className={styles.game_title}>{game.name}</h1>

                {/* 游玩按钮 */}
                <Button
                    className={styles.play_button}
                    onClick={handlePlayNow}
                    size="lg"
                    leftSection={<PixelArrow />}
                >
                    Play now
                </Button>

                {/* 推荐游戏 */}
                <div className={styles.similar_games_section}>
                    <GameSimilar gameList={similarGames} loading={loading} />
                </div>

                {/* 游戏详情 - 使用统一的 GameDescription 组件 */}
                <GameDescription game={game} isMobile={true} />
            </div>

            {showIframe && (
                <button 
                    className={`${styles.close_button} ${isButtonExpanded ? styles.expanded : ''}`}
                    onClick={(e) => {
                        handleCloseButtonClick(e);
                    }}
                    type="button">
                        {isButtonExpanded ? (
                            <DoorOpen color='#333333' size={24} />
                        ) : (
                            <ChevronRight color='#333333' size={24} />
                        )}
                </button>
            )}

            {/* 全屏游戏iframe */}
            {showIframe && (
                <div 
                    className={`${styles.fullscreen_game} ${isButtonExpanded ? styles.expanded : ''}`}
                    onClick={(e) => {
                        // 只有点击容器本身时才关闭，不是iframe或关闭按钮
                        if (e.target === e.currentTarget) {
                            handleCloseIframe(e);
                        }
                    }}
                >
                    <iframe
                        ref={iframeRef}
                        src={game.src}
                        title={game.name}
                        allow="fullscreen; autoplay; camera; microphone; gamepad"
                        allowFullScreen
                        className={styles.game_iframe}
                        onClick={(e) => {
                            // 阻止iframe点击事件冒泡
                            e.stopPropagation();
                        }}
                    />
                </div>
            )}
        </>
    );
}